import React, { useState, useRef, useEffect } from 'react';
import api from '../services/api';

const renderInlineMarkdown = (text, keyPrefix) => {
  const parts = String(text).split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);

  return parts.map((part, index) => {
    const key = `${keyPrefix}-${index}`;
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={key}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*')) {
      return <em key={key}>{part.slice(1, -1)}</em>;
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={key} className="rounded bg-black/10 px-1 py-0.5 text-sm">{part.slice(1, -1)}</code>;
    }
    return part;
  });
};

const MarkdownMessage = ({ text }) => (
  <div className="space-y-2 text-left">
    {String(text).split(/\n{2,}/).map((paragraph, paragraphIndex) => {
      const lines = paragraph.split('\n');
      const listItems = lines.filter(line => /^\s*(?:[-*]|\d+\.)\s+/.test(line));

      if (listItems.length === lines.length) {
        const ordered = /^\s*\d+\./.test(lines[0]);
        const List = ordered ? 'ol' : 'ul';
        return (
          <List key={paragraphIndex} className={`${ordered ? 'list-decimal' : 'list-disc'} space-y-1 pl-5`}>
            {lines.map((line, lineIndex) => (
              <li key={lineIndex}>{renderInlineMarkdown(line.replace(/^\s*(?:[-*]|\d+\.)\s+/, ''), `${paragraphIndex}-${lineIndex}`)}</li>
            ))}
          </List>
        );
      }

      return (
        <p key={paragraphIndex} className="whitespace-pre-wrap">
          {lines.map((line, lineIndex) => (
            <React.Fragment key={lineIndex}>
              {lineIndex > 0 && <br />}
              {renderInlineMarkdown(line, `${paragraphIndex}-${lineIndex}`)}
            </React.Fragment>
          ))}
        </p>
      );
    })}
  </div>
);

const AIChat = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [connectedApps, setConnectedApps] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    api.listApps()
      .then(data => {
        const licensed = Object.keys(data);
        const saved = JSON.parse(localStorage.getItem('ssdash_active_apps') || 'null');
        const selected = Array.isArray(saved)
          ? saved.filter(app => licensed.includes(app))
          : licensed;
        setConnectedApps(selected);
      })
      .catch(() => setConnectedApps([]));
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const userMessage = {
      text: inputMessage,
      sender: 'user',
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.sendMessage(inputMessage, sessionId, connectedApps);
      setSessionId(response.session_id);

      const aiResponse = {
        text: response.message,
        sender: 'ai',
        actions: response.actions || [],
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, aiResponse]);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const updateAction = (actionId, update) => {
    setMessages(prev => prev.map(message => ({
      ...message,
      actions: message.actions?.map(action => action.id === actionId ? { ...action, ...update } : action)
    })));
  };

  const handleAction = async (action, decision) => {
    updateAction(action.id, { status: decision === 'confirm' ? 'executing' : decision === 'retry' ? 'retrying' : 'rejecting' });
    try {
      const result = decision === 'confirm' ? await api.confirmAction(action.id)
        : decision === 'retry' ? await api.retryAction(action.id)
        : await api.rejectAction(action.id);
      updateAction(action.id, result);
    } catch (err) {
      updateAction(action.id, { status: 'failed', error: err.message });
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setSessionId(null);
    setError(null);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">AI Assistant</h2>
            <p className="text-sm text-gray-600">
              Ask me about your business data and applications
            </p>
          </div>
          {sessionId && (
            <button
              onClick={handleNewChat}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              New Chat
            </button>
          )}
        </div>
        {connectedApps.length > 0 ? (
          <div className="mt-2 p-2 bg-green-50 rounded-lg">
            <p className="text-xs text-green-700 font-medium">
              Current AI context: {connectedApps.join(', ')}
            </p>
          </div>
        ) : (
          <div className="mt-2 p-2 bg-orange-50 rounded-lg">
            <p className="text-xs text-orange-700">
              No apps enabled. Visit Manage Apps to enable your VentES apps.
            </p>
          </div>
        )}
      </div>

      <div className="h-64 overflow-y-auto mb-4 p-4 bg-gray-50 rounded-lg">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`mb-4 ${
              message.sender === 'user' ? 'text-right' : 'text-left'
            }`}
          >
            <div
              className={`inline-block p-3 rounded-lg ${
                message.sender === 'user'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-800'
              }`}
            >
              {message.sender === 'ai' ? <MarkdownMessage text={message.text} /> : message.text}
            </div>
            {message.actions?.filter(action => action.status !== 'completed').map(action => (
              <div key={action.id} className="mt-2 max-w-xl rounded-lg border border-amber-300 bg-amber-50 p-3 text-left text-gray-800">
                <div className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                  Proposed action
                </div>
                <div className="mt-1 font-medium">{action.summary}</div>
                <div className="mt-1 text-xs text-gray-600">{action.tool_name}</div>
                <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-white p-2 text-xs">{JSON.stringify(action.arguments, null, 2)}</pre>
                {action.error && <div className="mt-2 text-sm text-red-600">{action.error}</div>}
                {action.status === 'pending' ? (
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => handleAction(action, 'confirm')} className="rounded bg-green-600 px-3 py-1 text-sm text-white hover:bg-green-700">Confirm</button>
                    <button onClick={() => handleAction(action, 'reject')} className="rounded border border-gray-300 bg-white px-3 py-1 text-sm hover:bg-gray-50">Reject</button>
                  </div>
                ) : action.status === 'failed' ? (
                  <div className="mt-3">
                    <button onClick={() => handleAction(action, 'retry')} className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700">Prepare retry</button>
                  </div>
                ) : (
                  <div className="mt-2 text-sm font-medium">Status: {action.status}</div>
                )}
              </div>
            ))}
          </div>
        ))}
        {isLoading && (
          <div className="text-left">
            <div className="inline-block p-3 rounded-lg bg-gray-200">
              <div className="flex space-x-2">
                <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>
          </div>
        )}
        {error && (
          <div className="text-center text-red-500 mb-4">
            {error}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSubmit} className="flex space-x-2">
        <input
          type="text"
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          placeholder="Ask about your business data..."
          className="flex-1 p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        >
          Send
        </button>
      </form>
    </div>
  );
};

export default AIChat;
